import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { UserProfile } from "../contexts/UserContext";
import { useLanguage } from "../contexts/LanguageContext";
import { APP_ROUTES, clearReturnTarget } from "../routing/appRoutes";
import {
  getDebugModePreference,
  isDebugUserEmail,
  saveDebugModePreference,
} from "../services/debugMode";
import RouteFocusManager from "../routing/RouteFocusManager";
import LanguageSelector from "./LanguageSelector";

export interface AuthenticatedOutletContext {
  user: UserProfile;
  debugEligible: boolean;
  debugModeEnabled: boolean;
  libraryResetSignal: number;
  onDebugModeChange: (enabled: boolean) => void;
  onUpdateSettings: (name: string, targetLevel: string) => void;
  onDeleteAccount: () => Promise<void>;
}

interface AuthenticatedAppShellProps {
  user: UserProfile;
  onLogout: () => void;
  onDeleteAccount: () => Promise<void>;
  onUpdateUser: (updates: Partial<UserProfile>) => void;
}

export const AuthenticatedAppShell: React.FC<AuthenticatedAppShellProps> = ({
  user,
  onLogout,
  onDeleteAccount,
  onUpdateUser,
}) => {
  const appIconSrc = `${process.env.PUBLIC_URL}/logo192.png`;
  const { t } = useLanguage();
  const location = useLocation();
  const [debugModeEnabled, setDebugModeEnabled] = useState(false);
  const [libraryResetSignal, setLibraryResetSignal] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const debugEligible = isDebugUserEmail(user.email);

  useEffect(() => {
    setDebugModeEnabled(
      debugEligible ? getDebugModePreference(user.id) : false
    );
  }, [debugEligible, user.id]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.hash, location.pathname, location.search]);

  const onDebugModeChange = useCallback((enabled: boolean) => {
    if (!debugEligible) return;
    saveDebugModePreference(user.id, enabled);
    setDebugModeEnabled(enabled);
  }, [debugEligible, user.id]);

  const outletContext = useMemo<AuthenticatedOutletContext>(
    () => ({
      user,
      debugEligible,
      debugModeEnabled,
      libraryResetSignal,
      onDebugModeChange,
      onUpdateSettings: (name, targetLevel) =>
        onUpdateUser({ name, targetTier: targetLevel }),
      onDeleteAccount,
    }),
    [
      debugEligible,
      debugModeEnabled,
      libraryResetSignal,
      onDebugModeChange,
      onDeleteAccount,
      onUpdateUser,
      user,
    ]
  );

  const navItems = [
    { id: "library", to: APP_ROUTES.library, label: t("nav.library") },
    { id: "savedVocab", to: APP_ROUTES.savedVocab, label: t("nav.savedVocab") },
    { id: "history", to: APP_ROUTES.history, label: t("nav.history") },
    { id: "settings", to: APP_ROUTES.settings, label: t("nav.settings") },
  ];
  const isLibraryActive =
    location.pathname === APP_ROUTES.library ||
    location.pathname.startsWith(`${APP_ROUTES.library}/sections/`);

  const handleLibraryClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (location.pathname === APP_ROUTES.library) {
      event.preventDefault();
      setLibraryResetSignal((signal) => signal + 1);
    }
  };

  const handleLogout = () => {
    clearReturnTarget();
    onLogout();
  };

  const navLinkClassName = (active: boolean, mobile = false) =>
    mobile
      ? `flex min-h-11 w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm font-medium transition-colors ${
          active
            ? "bg-indigo-50 text-indigo-700"
            : "text-gray-700 hover:bg-gray-50"
        }`
      : `whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors ${
          active ? "bg-indigo-100 text-indigo-700" : "text-white hover:bg-white/20"
        }`;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-3">
            <div className="flex min-w-0 items-center">
              <img src={appIconSrc} alt="" className="mr-2 h-9 w-9 shrink-0 rounded-lg" />
              <span className="truncate text-xl font-bold text-white">{t("common.appName")}</span>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                {navItems.map((item) => {
                  const active = item.id === "library" ? isLibraryActive : location.pathname === item.to;
                  return (
                    <NavLink
                      key={item.id}
                      to={item.to}
                      end={item.id !== "library"}
                      onClick={item.id === "library" ? handleLibraryClick : undefined}
                      data-testid={`${item.id}-nav-tab`}
                      aria-current={active ? "page" : undefined}
                      className={navLinkClassName(active)}
                    >
                      {item.label}
                    </NavLink>
                  );
                })}
              </div>
            </div>
            <div className="hidden items-center space-x-4 md:flex">
              <LanguageSelector variant="dark" />
              <span className="max-w-48 truncate text-sm font-medium text-white">{t("nav.welcome", { name: user.name || "" })}</span>
              <button onClick={handleLogout} className="whitespace-nowrap bg-red-600 px-4 py-2 rounded-md text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 cursor-pointer transition-colors">
                {t("nav.logout")}
              </button>
            </div>
            <button type="button" onClick={() => setIsMobileMenuOpen((isOpen) => !isOpen)} className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-white/40 text-white transition-colors hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white md:hidden" aria-label={t("nav.openMenu")} aria-controls="mobile-navigation" aria-expanded={isMobileMenuOpen}>
              <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                {isMobileMenuOpen ? <><path d="M18 6 6 18" /><path d="m6 6 12 12" /></> : <><path d="M4 7h16" /><path d="M4 12h16" /><path d="M4 17h16" /></>}
              </svg>
            </button>
          </div>
        </div>
        {isMobileMenuOpen && (
          <div id="mobile-navigation" className="border-t border-white/30 bg-white px-4 py-3 shadow-lg md:hidden">
            <nav className="space-y-2" aria-label={t("nav.mobileLabel")}>
              {navItems.map((item) => {
                const active = item.id === "library" ? isLibraryActive : location.pathname === item.to;
                return (
                  <NavLink key={item.id} to={item.to} end={item.id !== "library"} onClick={item.id === "library" ? handleLibraryClick : undefined} data-testid={`${item.id}-mobile-nav-tab`} aria-current={active ? "page" : undefined} className={navLinkClassName(active, true)}>
                    <span>{item.label}</span>
                    {active && <span className="h-2 w-2 rounded-full bg-indigo-600" aria-hidden="true" />}
                  </NavLink>
                );
              })}
            </nav>
            <div className="mt-3 flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
              <span className="min-w-0 truncate text-sm font-medium text-gray-700">{t("nav.welcome", { name: user.name || "" })}</span>
              <button type="button" onClick={handleLogout} className="shrink-0 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2">{t("nav.logout")}</button>
            </div>
            <div className="mt-3 border-t border-gray-100 pt-3"><LanguageSelector /></div>
          </div>
        )}
      </header>
      <RouteFocusManager containerId="route-content" />
      <main id="route-content"><Outlet context={outletContext} /></main>
      <footer className="bg-white border-t mt-12">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0"><img src={appIconSrc} alt="" className="mr-2 h-8 w-8 rounded-md" /><span className="font-semibold text-lg text-gray-800">{t("common.appName")}</span></div>
            <div className="flex space-x-6"><a href="/" className="text-gray-500 hover:text-gray-700">Twitter</a><a href="/" className="text-gray-500 hover:text-gray-700">Facebook</a><a href="/" className="text-gray-500 hover:text-gray-700">Instagram</a><a href="/" className="text-gray-500 hover:text-gray-700">YouTube</a></div>
            <div className="mt-4 md:mt-0 text-sm text-gray-500">© 2026 PageBunny. All rights reserved.</div>
          </div>
        </div>
      </footer>
    </div>
  );
};
